<template>
    <div class="app-container">
      <div class="header">
        <h2><i class="el-icon-document-checked"></i> 患者随访记录管理</h2>
        <div class="header-info">
          <span>总记录数：{{ totalRecords }}条</span>
          <span>本月随访：{{ monthlyStats.total }}条</span>
          <span>已完成随访：{{ monthlyStats.completed }}条</span>
          <span>需要复查：{{ monthlyStats.needReview }}条</span>
        </div>
      </div>
  
      <!-- 搜索区域 -->
      <el-card class="search-card">
        <el-form :inline="true" :model="queryParams" ref="queryForm" size="small" label-width="90px">
          <el-form-item label="患者信息" prop="patientKeyword">
            <el-input
              v-model="queryParams.patientKeyword"
              placeholder="姓名/病历号/手机号"
              clearable
              style="width: 200px"
              @keyup.enter.native="handleQuery"
            />
          </el-form-item>
          
          <el-form-item label="随访类型" prop="followUpType">
            <el-select v-model="queryParams.followUpType" placeholder="请选择" clearable style="width: 150px">
              <el-option
                v-for="item in followUpTypeOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          
          <el-form-item label="随访日期" prop="dateRange">
            <el-date-picker
              v-model="queryParams.dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              style="width: 240px"
            />
          </el-form-item>
          
          <el-form-item label="随访人员" prop="staffName">
            <el-input
              v-model="queryParams.staffName"
              placeholder="随访人员姓名"
              clearable
              style="width: 150px"
            />
          </el-form-item>
          
          <el-form-item label="随访状态" prop="status">
            <el-select v-model="queryParams.status" placeholder="请选择" clearable style="width: 120px">
              <el-option
                v-for="item in statusOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleQuery">搜索</el-button>
            <el-button icon="el-icon-refresh" @click="resetQuery">重置</el-button>
          </el-form-item>
        </el-form>
      </el-card>
  
      <!-- 数据统计 -->
      <div class="statistics-container">
        <el-row :gutter="20">
          <el-col :span="6">
            <el-card shadow="hover" class="statistic-card">
              <div slot="header" class="clearfix">
                <span>本季度随访记录统计</span>
              </div>
              <div class="statistic-content">
                <div class="statistic-value">{{ statistics.quarterlyTotal }}</div>
                <div class="statistic-chart">
                  <div class="chart-item">
                    <div class="chart-bar" :style="{ width: statistics.quarterlyCompletionRate + '%', backgroundColor: '#67C23A' }"></div>
                    <div class="chart-label">完成率: {{ statistics.quarterlyCompletionRate }}%</div>
                  </div>
                </div>
              </div>
            </el-card>
          </el-col>
          <el-col :span="6">
            <el-card shadow="hover" class="statistic-card">
              <div slot="header" class="clearfix">
                <span>随访满意度</span>
              </div>
              <div class="statistic-content">
                <div class="statistic-value">{{ statistics.satisfactionRate }}%</div>
                <div class="satisfaction-rating">
                  <i class="el-icon-star-on" v-for="index in 5" :key="index" :style="{ color: index <= statistics.satisfactionStars ? '#F7BA2A' : '#EAEAEA' }"></i>
                </div>
              </div>
            </el-card>
          </el-col>
          <el-col :span="6">
            <el-card shadow="hover" class="statistic-card">
              <div slot="header" class="clearfix">
                <span>本月随访方式分布</span>
              </div>
              <div class="statistic-content">
                <div class="statistic-pie">
                  <div class="pie-item">
                    <div class="pie-segment" style="background-color: #409EFF; transform: rotate(0deg) skew(30deg);"></div>
                    <div class="pie-segment" style="background-color: #67C23A; transform: rotate(120deg) skew(30deg);"></div>
                    <div class="pie-segment" style="background-color: #E6A23C; transform: rotate(240deg) skew(30deg);"></div>
                  </div>
                </div>
                <div class="pie-legend">
                  <div class="legend-item"><span class="color-box" style="background-color: #409EFF"></span>电话: {{ statistics.methodDistribution.phone }}%</div>
                  <div class="legend-item"><span class="color-box" style="background-color: #67C23A"></span>门诊: {{ statistics.methodDistribution.outpatient }}%</div>
                  <div class="legend-item"><span class="color-box" style="background-color: #E6A23C"></span>家访: {{ statistics.methodDistribution.homeVisit }}%</div>
                </div>
              </div>
            </el-card>
          </el-col>
          <el-col :span="6">
            <el-card shadow="hover" class="statistic-card">
              <div slot="header" class="clearfix">
                <span>待随访提醒</span>
              </div>
              <div class="statistic-content">
                <div class="statistic-value">{{ statistics.pendingFollowUps }}</div>
                <div class="pending-info">
                  <div>今日待随访: <span>{{ statistics.todayPending }}</span></div>
                  <div>下周到期: <span>{{ statistics.nextWeekPending }}</span></div>
                </div>
                <el-button type="text" icon="el-icon-view" class="view-pending">查看详情</el-button>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>
  
      <!-- 表格工具栏 -->
      <div class="table-operations">
        <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增随访</el-button>
        <el-button type="success" icon="el-icon-upload2" size="small" @click="handleExport">导出Excel</el-button>
        <el-button type="info" icon="el-icon-s-data" size="small" @click="showReportDialog">生成报表</el-button>
        <el-dropdown split-button size="small" @command="handleBatchCommand" style="margin-left: 8px;">
          <span>批量操作</span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="markCompleted">标记完成</el-dropdown-item>
            <el-dropdown-item command="schedule">安排下次随访</el-dropdown-item>
            <el-dropdown-item command="assignStaff">分配随访人员</el-dropdown-item>
            <el-dropdown-item command="delete" divided>批量删除</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        
        <div style="float: right;">
          <el-radio-group v-model="viewMode" size="small">
            <el-radio-button label="all">全部记录</el-radio-button>
            <el-radio-button label="today">今日随访</el-radio-button>
            <el-radio-button label="pending">待处理</el-radio-button>
            <el-radio-button label="priority">重点关注</el-radio-button>
          </el-radio-group>
        </div>
      </div>
      
      <!-- 数据表格 -->
      <el-table
        v-loading="loading"
        :data="followUpList"
        @selection-change="handleSelectionChange"
        :row-class-name="tableRowClassName"
        style="width: 100%"
      >
        <el-table-column type="selection" width="55" align="center" />
        <el-table-column label="随访ID" align="center" prop="followUpId" width="100" />
        <el-table-column label="患者姓名" align="center" prop="patientName" width="100">
          <template slot-scope="scope">
            <el-tooltip :content="'病历号: ' + scope.row.medicalRecordNo" placement="top">
              <span>{{ scope.row.patientName }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="性别/年龄" align="center" prop="genderAge" width="80" />
        <el-table-column label="诊断疾病" align="center" prop="diagnosis" width="150" :show-overflow-tooltip="true" />
        <el-table-column label="随访类型" align="center" prop="followUpType" width="100">
          <template slot-scope="scope">
            <el-tag :type="getFollowUpTypeTag(scope.row.followUpType)">{{ scope.row.followUpType }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="随访方式" align="center" prop="followUpMethod" width="100" />
        <el-table-column label="随访日期" align="center" prop="followUpDate" width="100" />
        <el-table-column label="随访人员" align="center" prop="staffName" width="100">
          <template slot-scope="scope">
            <el-tooltip :content="'科室: ' + scope.row.department" placement="top">
              <span>{{ scope.row.staffName }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="随访结果" align="center" prop="followUpResult" width="120">
          <template slot-scope="scope">
            <el-tag :type="getResultTag(scope.row.followUpResult)">{{ scope.row.followUpResult }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="下次随访" align="center" prop="nextFollowUpDate" width="100" />
        <el-table-column label="状态" align="center" prop="status" width="80">
          <template slot-scope="scope">
            <el-tag :type="getStatusTag(scope.row.status)">{{ scope.row.status }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" align="center" width="260">
          <template slot-scope="scope">
            <el-button size="mini" type="text" icon="el-icon-view" @click="handleViewDetail(scope.row)">查看</el-button>
            <el-button size="mini" type="text" icon="el-icon-edit" @click="handleEdit(scope.row)">编辑</el-button>
            <el-dropdown size="mini" split-button type="text" @command="(command) => handleCommand(command, scope.row)">
              <span>更多</span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="complete" v-if="scope.row.status !== '已完成'">标记完成</el-dropdown-item>
                <el-dropdown-item command="schedule">安排下次随访</el-dropdown-item>
                <el-dropdown-item command="priority" :disabled="scope.row.isPriority">
                  {{ scope.row.isPriority ? '取消重点关注' : '标记重点关注' }}
                </el-dropdown-item>
                <el-dropdown-item command="delete" divided>删除</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <pagination
        v-show="total > 0"
        :total="total"
        :page.sync="queryParams.pageNum"
        :limit.sync="queryParams.pageSize"
        @pagination="getList"
      />
  
      <!-- 查看随访记录详情对话框 -->
      <el-dialog :title="`随访记录详情 - ${detailForm.patientName}`" :visible.sync="detailDialogVisible" width="800px">
        <el-descriptions class="margin-top" title="患者基本信息" :column="3" border>
          <el-descriptions-item>
            <template slot="label">患者姓名</template>
            {{ detailForm.patientName }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label">性别/年龄</template>
            {{ detailForm.genderAge }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label">病历号</template>
            {{ detailForm.medicalRecordNo }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label">联系电话</template>
            {{ detailForm.contactNumber }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label">诊断疾病</template>
            {{ detailForm.diagnosis }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label">主治医生</template>
            {{ detailForm.primaryDoctor }}
          </el-descriptions-item>
        </el-descriptions>
  
        <el-divider content-position="left">随访信息</el-divider>
        
        <el-descriptions :column="3" border>
          <el-descriptions-item>
            <template slot="label">随访类型</template>
            {{ detailForm.followUpType }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label">随访方式</template>
            {{ detailForm.followUpMethod }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label">随访日期</template>
            {{ detailForm.followUpDate }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label">随访人员</template>
            {{ detailForm.staffName }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label">所属科室</template>
            {{ detailForm.department }}
          </el-descriptions-item>
          <el-descriptions-item>
            <template slot="label">随访结果</template>
            <el-tag :type="getResultTag(detailForm.followUpResult)">{{ detailForm.followUpResult }}</el-tag>
          </el-descriptions-item>
        </el-descriptions>
  
        <el-divider content-position="left">随访内容</el-divider>
        
        <div class="follow-up-content">
          <h4>随访目的</h4>
          <p>{{ detailForm.purpose || '无' }}</p>
          
          <h4>患者当前情况</h4>
          <p>{{ detailForm.currentCondition || '无' }}</p>
          
          <h4>用药情况</h4>
          <el-table :data="detailForm.medications || []" style="width: 100%" size="small" border>
            <el-table-column prop="name" label="药品名称" width="180" />
            <el-table-column prop="dosage" label="用量" width="120" />
            <el-table-column prop="frequency" label="用药频次" width="120" />
            <el-table-column prop="duration" label="用药时长" width="120" />
            <el-table-column prop="notes" label="备注" />
          </el-table>
          
          <h4>随访意见</h4>
          <p>{{ detailForm.advice || '无' }}</p>
          
          <h4>下一步计划</h4>
          <el-row :gutter="20">
            <el-col :span="12">
              <div class="next-step-item">
                <label>下次随访时间：</label>
                <span>{{ detailForm.nextFollowUpDate || '暂未安排' }}</span>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="next-step-item">
                <label>是否需要复查：</label>
                <span>{{ detailForm.needReexamination ? '是' : '否' }}</span>
              </div>
            </el-col>
            <el-col :span="24">
              <div class="next-step-item">
                <label>复查项目：</label>
                <span>{{ detailForm.reexaminationItems || '无' }}</span>
              </div>
            </el-col>
          </el-row>
          
          <h4>备注信息</h4>
          <p>{{ detailForm.remarks || '无' }}</p>
        </div>
  
        <div slot="footer" class="dialog-footer">
          <el-button @click="detailDialogVisible = false">关闭</el-button>
          <el-button type="primary" @click="handleDownloadRecord">导出记录</el-button>
        </div>
      </el-dialog>
    </div>
  </template>
  
  <script>
  export default {
    name: "FollowUpRecord",
    components: {
      // 假设有一个分页组件
      Pagination: {
        template: `
          <div class="pagination-container">
            <el-pagination
              :current-page="page"
              :page-sizes="[10, 20, 30, 50]"
              :page-size="limit"
              :total="total"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
            ></el-pagination>
          </div>
        `,
        props: {
          total: {
            type: Number,
            default: 0
          },
          page: {
            type: Number,
            default: 1
          },
          limit: {
            type: Number,
            default: 10
          }
        },
        methods: {
          handleSizeChange(val) {
            this.$emit('update:limit', val);
            this.$emit('pagination');
          },
          handleCurrentChange(val) {
            this.$emit('update:page', val);
            this.$emit('pagination');
          }
        }
      }
    },
    data() {
      return {
        // 遮罩层
        loading: false,
        // 选中数组
        ids: [],
        // 选中行数据
        selection: [],
        // 总条数
        total: 0,
        // 总记录数
        totalRecords: 0,
        // 本月统计数据
        monthlyStats: {
          total: 235,
          completed: 189,
          needReview: 37,
        },
        // 视图模式
        viewMode: 'all',
        // 统计数据
        statistics: {
          quarterlyTotal: 658,
          quarterlyCompletionRate: 82,
          satisfactionRate: 92,
          satisfactionStars: 5,
          methodDistribution: {
            phone: 65,
            outpatient: 25,
            homeVisit: 10
          },
          pendingFollowUps: 78,
          todayPending: 12,
          nextWeekPending: 45
        },
        // 随访记录列表
        followUpList: [],
        // 查询参数
        queryParams: {
          pageNum: 1,
          pageSize: 10,
          patientKeyword: '',
          followUpType: '',
          dateRange: [],
          staffName: '',
          status: ''
        },
        // 详情对话框
        detailDialogVisible: false,
        // 详情表单
        detailForm: {},
        // 随访类型选项
        followUpTypeOptions: [
          { value: '出院随访', label: '出院随访' },
          { value: '慢病随访', label: '慢病随访' },
          { value: '术后随访', label: '术后随访' },
          { value: '产后随访', label: '产后随访' },
          { value: '精神科随访', label: '精神科随访' },
          { value: '其他', label: '其他' }
        ],
        // 状态选项
        statusOptions: [
          { value: '已完成', label: '已完成' },
          { value: '未完成', label: '未完成' },
          { value: '已取消', label: '已取消' },
          { value: '待随访', label: '待随访' }
        ]
      };
    },
    created() {
      this.getList();
    },
    methods: {
      // 查询随访记录列表
      getList() {
        this.loading = true;
        // 模拟API请求
        setTimeout(() => {
          // 使用模拟数据
          this.followUpList = this.generateMockData();
          this.total = this.followUpList.length;
          this.totalRecords = 1358;
          this.loading = false;
        }, 500);
      },
      // 生成模拟数据
      generateMockData() {
        const mockData = [];
        const patientNames = ['许昊然', '苏雅婷', '林书辰', '程若曦', '郭子轩', '韩宇航', '江语涵', '沈思妍', '郑十一', '王明', '李华', '张芳'];
        const genders = ['男', '女'];
        const ages = [45, 67, 32, 58, 73, 29, 61, 55, 42, 38, 71, 52];
        const diagnoses = ['高血压', '2型糖尿病', '冠心病', '骨折术后', '肺炎', '肝功能异常', '甲状腺功能减退', '慢性胃炎', '癫痫', '类风湿关节炎', '前列腺炎'];
        const followUpTypes = ['出院随访', '慢病随访', '术后随访', '产后随访', '精神科随访', '其他'];
        const followUpMethods = ['电话', '门诊', '家访', '视频会诊'];
        const followUpResults = ['良好', '一般', '不佳', '需复诊', '失联'];
        const statuses = ['已完成', '未完成', '已取消', '待随访'];
        
        for (let i = 1; i <= 20; i++) {
          const randomPatientIndex = Math.floor(Math.random() * patientNames.length);
          const randomGender = genders[Math.floor(Math.random() * genders.length)];
          const randomAge = ages[Math.floor(Math.random() * ages.length)];
          const randomDiagnosis = diagnoses[Math.floor(Math.random() * diagnoses.length)];
          const randomFollowUpType = followUpTypes[Math.floor(Math.random() * followUpTypes.length)];
          const randomFollowUpMethod = followUpMethods[Math.floor(Math.random() * followUpMethods.length)];
          const randomFollowUpResult = followUpResults[Math.floor(Math.random() * followUpResults.length)];
          const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
          const isPriority = Math.random() > 0.8;
          
          // 生成随机日期
          const today = new Date();
          const randomDaysAgo = Math.floor(Math.random() * 30);
          const randomDaysLater = Math.floor(Math.random() * 30) + 1;
          const followUpDate = new Date(today);
          followUpDate.setDate(today.getDate() - randomDaysAgo);
          const nextFollowUpDate = new Date(today);
          nextFollowUpDate.setDate(today.getDate() + randomDaysLater);
          
          mockData.push({
            followUpId: 'FU' + (1000 + i).toString(),
            patientName: patientNames[randomPatientIndex],
            genderAge: `${randomGender}/${randomAge}岁`,
            medicalRecordNo: 'MR' + Math.floor(100000 + Math.random() * 900000),
            contactNumber: '1' + Math.floor(3000000000 + Math.random() * 7000000000),
            diagnosis: randomDiagnosis,
            followUpType: randomFollowUpType,
            followUpMethod: randomFollowUpMethod,
            followUpDate: this.formatDate(followUpDate),
            staffName: this.getRandomStaffName(),
            department: this.getRandomDepartment(),
            followUpResult: randomFollowUpResult,
            nextFollowUpDate: randomFollowUpResult === '需复诊' ? this.formatDate(nextFollowUpDate) : '',
            status: randomStatus,
            isPriority: isPriority,
            primaryDoctor: this.getRandomStaffName(),
            purpose: '评估患者目前病情恢复情况，确认治疗方案是否有效，检查是否有不良反应。',
            currentCondition: '患者自述症状有所缓解，但仍存在' + randomDiagnosis + '相关症状，生活能够基本自理。',
            medications: [
              { name: '药品A', dosage: '5mg', frequency: '每日2次', duration: '持续服用', notes: '' },
              { name: '药品B', dosage: '10mg', frequency: '每日1次', duration: '3个月', notes: '饭后服用' },
            ],
            advice: '建议继续按医嘱服药，规律作息，避免过度疲劳，保持良好的生活习惯。',
            needReexamination: Math.random() > 0.5,
            reexaminationItems: '血常规、肝功能、肾功能',
            remarks: Math.random() > 0.7 ? '患者心理状态需要关注，建议下次随访增加心理评估。' : ''
          });
        }
        
        return mockData;
      },
      // 格式化日期
      formatDate(date) {
        const year = date.getFullYear();
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const day = ('0' + date.getDate()).slice(-2);
        return `${year}-${month}-${day}`;
      },
      // 随机生成随访人员姓名
      getRandomStaffName() {
        const lastNames = ['张', '李', '王', '赵', '陈', '刘', '杨', '黄', '吴', '周'];
        const firstNames = ['医生', '主任', '护士', '医师'];
        return lastNames[Math.floor(Math.random() * lastNames.length)] + firstNames[Math.floor(Math.random() * firstNames.length)];
      },
      // 随机生成科室
      getRandomDepartment() {
        const departments = ['内科', '外科', '儿科', '妇产科', '神经内科', '心内科', '骨科', '精神科', '眼科', '耳鼻喉科'];
        return departments[Math.floor(Math.random() * departments.length)];
      },
      // 搜索按钮操作
      handleQuery() {
        this.queryParams.pageNum = 1;
        this.getList();
      },
      // 重置按钮操作
      resetQuery() {
        this.queryParams = {
          pageNum: 1,
          pageSize: 10,
          patientKeyword: '',
          followUpType: '',
          dateRange: [],
          staffName: '',
          status: ''
        };
        this.handleQuery();
      },
      // 多选框选中数据
      handleSelectionChange(selection) {
        this.selection = selection;
        this.ids = selection.map(item => item.followUpId);
      },
      // 表格行样式
      tableRowClassName({row}) {
        if (row.isPriority) {
          return 'priority-row';
        } else if (row.followUpResult === '需复诊') {
          return 'reexamination-row';
        }
        return '';
      },
      // 获取随访类型对应的Tag类型
      getFollowUpTypeTag(type) {
        const typeMap = {
          '出院随访': 'primary',
          '慢病随访': 'success',
          '术后随访': 'warning',
          '产后随访': 'info',
          '精神科随访': 'danger',
          '其他': ''
        };
        return typeMap[type] || '';
      },
      // 获取随访结果对应的Tag类型
      getResultTag(result) {
        const resultMap = {
          '良好': 'success',
          '一般': 'info',
          '不佳': 'warning',
          '需复诊': 'danger',
          '失联': ''
        };
        return resultMap[result] || '';
      },
      // 获取状态对应的Tag类型
      getStatusTag(status) {
        const statusMap = {
          '已完成': 'success',
          '未完成': 'info',
          '已取消': 'danger',
          '待随访': 'warning'
        };
        return statusMap[status] || '';
      },
      // 查看详情
      handleViewDetail(row) {
        this.detailForm = JSON.parse(JSON.stringify(row));
        this.detailDialogVisible = true;
      },
      // 编辑随访记录
      handleEdit(row) {
        this.$message.info(`编辑随访记录 ID: ${row.followUpId}`);
      },
      // 新增随访记录
      handleAdd() {
        this.$message.info('新增随访记录');
      },
      // 导出Excel
      handleExport() {
        this.$message.success('导出随访记录成功');
      },
      // 下载单个随访记录
      handleDownloadRecord() {
        this.$message.success(`成功导出随访记录: ${this.detailForm.followUpId}`);
        this.detailDialogVisible = false;
      },
      // 更多操作下拉菜单
      handleCommand(command, row) {
        switch (command) {
          case 'complete':
            this.$message.success(`已将随访 ${row.followUpId} 标记为完成`);
            break;
          case 'schedule':
            this.$message.info(`为患者 ${row.patientName} 安排下次随访`);
            break;
          case 'priority':
            this.$message.warning(`${row.isPriority ? '取消' : '标记'}患者 ${row.patientName} 为重点关注`);
            break;
          case 'delete':
            this.$confirm(`确认删除随访记录 ${row.followUpId}？`, '警告', {
              confirmButtonText: '确定',
              cancelButtonText: '取消',
              type: 'warning'
            }).then(() => {
              this.$message.success(`成功删除随访记录 ${row.followUpId}`);
            }).catch(() => {});
            break;
        }
      },
      // 批量操作命令
      handleBatchCommand(command) {
        if (this.ids.length === 0) {
          this.$message.warning('请选择需要操作的记录');
          return;
        }
        
        switch (command) {
          case 'markCompleted':
            this.$message.success(`成功将 ${this.ids.length} 条记录标记为已完成`);
            break;
          case 'schedule':
            this.$message.info(`为 ${this.ids.length} 名患者安排下次随访`);
            break;
          case 'assignStaff':
            this.$message.info(`为 ${this.ids.length} 条记录分配随访人员`);
            break;
          case 'delete':
            this.$confirm(`确认删除 ${this.ids.length} 条随访记录？`, '警告', {
              confirmButtonText: '确定',
              cancelButtonText: '取消',
              type: 'warning'
            }).then(() => {
              this.$message.success(`成功删除 ${this.ids.length} 条记录`);
            }).catch(() => {});
            break;
        }
      },
      // 显示报表对话框
      showReportDialog() {
        this.$message.info('生成随访统计报表');
      }
    }
  };
  </script>
  
  <style scoped>
  .app-container {
    padding: 20px;
  }
  
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 10px;
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  }
  
  .header h2 {
    margin: 0;
    font-size: 20px;
    color: #303133;
  }
  
  .header-info {
    display: flex;
    gap: 15px;
  }
  
  .header-info span {
    font-size: 14px;
    color: #606266;
  }
  
  .search-card {
    margin-bottom: 20px;
  }
  
  .statistics-container {
    margin-bottom: 20px;
  }
  
  .statistic-card {
    height: 250px;
  }
  
  .statistic-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: center;
  }
  
  .statistic-value {
    font-size: 28px;
    font-weight: bold;
    color: #303133;
    text-align: center;
    margin-bottom: 10px;
  }
  
  .statistic-chart {
    margin-top: 10px;
  }
  
  .chart-item {
    margin-bottom: 5px;
  }
  
  .chart-bar {
    height: 10px;
    background-color: #67C23A;
    border-radius: 5px;
    margin-bottom: 5px;
  }
  
  .chart-label {
    font-size: 12px;
    color: #909399;
    text-align: right;
  }
  
  .satisfaction-rating {
    text-align: center;
    font-size: 20px;
    margin-top: 5px;
  }
  
  .statistic-pie {
    position: relative;
    width: 60px;
    height: 60px;
    margin: 0 auto;
  }
  
  .pie-item {
    width: 100%;
    height: 100%;
    position: relative;
    border-radius: 50%;
    overflow: hidden;
  }
  
  .pie-segment {
    position: absolute;
    width: 100%;
    height: 100%;
    transform-origin: center;
  }
  
  .pie-legend {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  
  .legend-item {
    display: flex;
    align-items: center;
    font-size: 12px;
  }
  
  .color-box {
    width: 12px;
    height: 12px;
    margin-right: 5px;
    display: inline-block;
  }
  
  .pending-info {
    display: flex;
    justify-content: space-around;
    margin: 10px 0;
    font-size: 14px;
  }
  
  .pending-info span {
    font-weight: bold;
    color: #F56C6C;
  }
  
  .view-pending {
    margin-top: 5px;
    display: block;
    text-align: center;
  }
  
  .table-operations {
    margin-bottom: 15px;
    display: flex;
    align-items: center;
  }
  
  .priority-row {
    background-color: #FEF0F0 !important;
  }
  
  .reexamination-row {
    background-color: #F0F9EB !important;
  }
  
  .follow-up-content {
    margin-top: 20px;
  }
  
  .follow-up-content h4 {
    margin-top: 15px;
    margin-bottom: 10px;
    font-size: 16px;
    color: #303133;
    font-weight: 500;
  }
  
  .follow-up-content p {
    margin: 5px 0 15px;
    color: #606266;
    line-height: 1.6;
  }
  
  .next-step-item {
    margin-bottom: 10px;
  }
  
  .next-step-item label {
    font-weight: bold;
    margin-right: 5px;
    color: #606266;
  }
  
  .pagination-container {
    padding: 15px 0;
    text-align: right;
  }
  </style>